// v6 F9.8 — AuthCallback handles `#session_id=...` in URL fragment.
// v6 F11   — Robuste error handling: timeout, retry, user-friendly errors,
//            auto-redirect na fatale fout. Werkt op iPhone Safari, Android
//            Chrome, en alle desktop-browsers.
//
// CRITICAL: useRef + synchronous check prevents StrictMode double-processing.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { authFetch, describeAuthError } from "../lib/authFetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Maximum tijd dat we op een geldige session-exchange wachten. Boven deze
// drempel tonen we een fout met retry-knop i.p.v. eindeloos "Inloggen…".
const HARD_TIMEOUT_MS = 30000;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null); // sec voor auto-redirect

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Hard fail-safe: als we na 30s nog steeds in deze callback zitten,
    // toon error en laat gebruiker manueel terugkeren.
    const hardTimer = setTimeout(() => {
      if (!processed.current_done) {
        setError("De inlogprocedure duurt te lang. Controleer je internetverbinding en probeer opnieuw.");
      }
    }, HARD_TIMEOUT_MS);

    (async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      const sessionId = m ? decodeURIComponent(m[1]) : null;
      if (!sessionId) {
        setError("Geen geldige inlog-ticket ontvangen. Klik op 'Terug naar inloggen' en probeer opnieuw.");
        clearTimeout(hardTimer);
        return;
      }
      try {
        const r = await authFetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "X-Session-ID": sessionId },
        }, { timeoutMs: 15000, retries: 2 });

        if (r.status === 403) {
          // Beta volzet
          const data = await r.json().catch(() => ({}));
          throw new Error(data?.detail || "De beta is momenteel volzet.");
        }
        if (r.status === 401) {
          throw new Error("De inlog-ticket is ongeldig of verlopen. Probeer opnieuw in te loggen.");
        }
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data?.detail || `Inloggen mislukt (${r.status}).`);
        }
        await refresh();
        processed.current_done = true;
        clearTimeout(hardTimer);
        // Clear the fragment from URL history then navigate to dashboard.
        window.history.replaceState({}, "", "/");
        navigate("/", { replace: true });
      } catch (e) {
        clearTimeout(hardTimer);
        console.error("[emma:auth] session exchange failed", e);
        setError(describeAuthError(e));
      }
    })();

    return () => clearTimeout(hardTimer);
  }, [navigate, refresh]);

  // v6 F11 — Auto-redirect countdown na fout (8s). Gebruiker kan ook
  // direct klikken via de knop.
  useEffect(() => {
    if (!error) return;
    setCountdown(8);
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c === null) return null;
        if (c <= 1) {
          clearInterval(t);
          window.location.href = "/login";
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [error]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-5">
      <div className="w-full max-w-md bg-white border border-[#E5E2DC] rounded-2xl p-8 text-center" data-testid="auth-callback-card">
        {!error ? (
          <>
            <div className="flex justify-center mb-4">
              <span className="inline-block w-8 h-8 border-2 border-[#D96C4A] border-t-transparent rounded-full animate-spin" />
            </div>
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#5D7259]">
              Even geduld
            </div>
            <div className="font-heading text-xl text-[#2C2C2A] mt-2">
              Inloggen…
            </div>
            <p className="text-xs text-[#5D7259] mt-3">
              Eerste login na een tijd kan tot 10 sec duren.
            </p>
          </>
        ) : (
          <>
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#D9534F]" data-testid="auth-error-title">
              Inloggen mislukt
            </div>
            <div className="font-heading text-lg text-[#2C2C2A] mt-2 leading-snug" data-testid="auth-error-message">
              {error}
            </div>
            <button
              onClick={() => window.location.href = "/login"}
              className="mt-5 w-full h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] text-white font-medium"
              data-testid="auth-back-to-login"
            >
              Opnieuw proberen
            </button>
            {countdown !== null && countdown > 0 && (
              <p className="text-[11px] text-[#5D7259] mt-3" data-testid="auth-countdown">
                Auto-redirect over {countdown}s…
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
